import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LucideIcon, Loader2, XCircle, Wifi, WifiOff, Eye } from 'lucide-react';
import { automationLogService } from '../services/automationLogService';
import { employeesService } from '../services/employeesService';
import AiStatusBanner from './AiStatusBanner';
import AccountMenu from './AccountMenu';
import ThemeToggle from './ThemeToggle';
import { AlertBell } from '../features/alerts/AlertBell';
import { whenVisible } from '@/utils/whenVisible';
import { CompanyMultiSelect } from './ui/CompanyMultiSelect';
import {
  useCompanyFilter,
  COMPANY_FILTER_ROUTES,
} from '../contexts/CompanyFilterContext';

interface HeaderProps {
  title: string;
  subtitle?: string;
  actionButton?: {
    text: string;
    icon: LucideIcon;
    onClick: () => void;
  };
  hideVmStatus?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, actionButton, hideVmStatus = false }) => {
  const { user, logout, hasCap, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    showSelector: showCompanySelector,
    availableCompanies,
    selectedCompanyIds,
    setSelectedCompanyIds,
  } = useCompanyFilter();
  const insideAdminPanel = location.pathname.startsWith('/admin');
  // Só nas rotas que consomem o filtro — nas demais o seletor seria inerte.
  const companyFilterApplies = COMPANY_FILTER_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );
  const [vmStatus, setVmStatus] = useState<{
    online: boolean;
    url?: string;
    message?: string;
  }>(() => {
    try {
      const stored = sessionStorage.getItem('vmStatus');
      const storedTimestamp = sessionStorage.getItem('vmStatusTimestamp');
      if (stored && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        if (Date.now() - timestamp < 300000) {
          return JSON.parse(stored);
        }
      }
    } catch {
      /* ignore */
    }
    return { online: true };
  });

  // Contadores de status - calculados da mesma forma que no Painel de Automações (apenas para Desenvolvedor)
  // react-query: refetch a cada 30s e pausa sozinho quando a aba está em background
  // (refetchIntervalInBackground é false por padrão no react-query v3).
  const { data: statusCounts = null } = useQuery(
    ['header-automation-status-counts'],
    async () => {
      // Buscar muitos logs (sem filtros) para ter contadores precisos — mesma lógica do Painel de Automações
      const response = await automationLogService.getExecutionHistory({
        limit: 50,
        offset: 0,
      });
      const logs = response.logs;
      return {
        running: logs.filter(log => log.status === 'RUNNING').length,
        finished: logs.filter(log => log.status === 'FINISHED').length,
        failed: logs.filter(log => log.status === 'FAILED').length,
      };
    },
    {
      enabled: !!user && hasCap('can_use_dev_tools'),
      refetchInterval: 30000,
    },
  );

  useEffect(() => {
    if (!hasCap('can_use_dev_tools')) {
      return;
    }

    const checkVmStatus = async () => {
      try {
        const status = await automationLogService.getVmStatus();
        const newStatus = { online: status.online, url: status.url, message: status.message };
        setVmStatus(newStatus);
        sessionStorage.setItem('vmStatus', JSON.stringify(newStatus));
        sessionStorage.setItem('vmStatusTimestamp', Date.now().toString());
      } catch {
        const errorStatus = { online: false, message: 'Erro ao verificar status' };
        setVmStatus(errorStatus);
        sessionStorage.setItem('vmStatus', JSON.stringify(errorStatus));
        sessionStorage.setItem('vmStatusTimestamp', Date.now().toString());
      }
    };

    const storedTimestamp = sessionStorage.getItem('vmStatusTimestamp');
    const now = Date.now();
    const shouldCheck = !storedTimestamp || (now - parseInt(storedTimestamp, 10)) >= 300000;

    if (shouldCheck) {
      // Fazer verificação imediata apenas se necessário
      checkVmStatus();
    }

    // Verificar a cada 5 minutos (300000ms), só com a aba em primeiro plano
    const interval = setInterval(whenVisible(checkVmStatus), 300000);
    return () => clearInterval(interval);
  }, [hasCap]);

  const handleExitViewAs = async () => {
    try {
      await employeesService.updateMe({ view_as_company_id: null });
      await refreshUser();
    } catch (error) {
      console.error('Erro ao sair do modo de visualização:', error);
    }
  };

  // Dentro do /admin/*, o AdminPanel tem seu próprio header completo (com
  // foto, nome, role e status da VM). Renderizamos null para evitar header
  // duplicado quando páginas legadas (ProcessTypesManagement, JobManagement,
  // etc.) são embrulhadas como sub-views do painel.
  if (insideAdminPanel) {
    return null;
  }

  return (
    <>
      {user?.view_as_company_id != null && (
        <div className="w-full bg-amber-500 text-white text-sm px-6 py-1.5 flex items-center justify-center gap-3 flex-shrink-0">
          <Eye className="w-4 h-4" />
          <span>
            Visualizando como externo:{' '}
            <strong>
              {user.view_as_company_name ?? `empresa #${user.view_as_company_id}`}
            </strong>
          </span>
          <button
            onClick={handleExitViewAs}
            className="underline font-medium hover:text-amber-100"
            type="button"
          >
            Voltar
          </button>
        </div>
      )}
      {/* relative z-[75] (hub): os menus do header (sino, conta, empresas) ficam
          acima do conteúdo das páginas (FiltersBar z-[60], toolbar z-70) e abaixo
          dos modais (z-[80]+). */}
      <header
        className="relative z-[75] h-16 w-full flex-shrink-0 flex items-center bg-surface border-b border-[#e4e9f0] dark:border-border-faint"
        style={{ padding: '0 26px' }}
      >
        <div className="flex items-center justify-between w-full gap-4">

          {/* Left — title + subtitle */}
          <div className="flex-1 min-w-0">
            <h1
              className="font-bold truncate text-body"
              style={{
                fontSize: 21,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {title}
            </h1>
          {subtitle && (
            <p
              className="truncate mt-0.5 text-muted"
              style={{ fontSize: 13, lineHeight: 1.3 }}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* Right — status + actions + user */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <AiStatusBanner />

          {actionButton && (
            <button
              onClick={actionButton.onClick}
              className="flex items-center gap-2 text-white font-semibold transition-all"
              style={{
                fontSize: 13,
                padding: '8px 14px',
                borderRadius: 5,
                background: '#2563eb',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 10px rgba(37,99,235,0.35)',
              }}
              onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.08)')}
              onMouseLeave={e => (e.currentTarget.style.filter = '')}
              type="button"
            >
              <actionButton.icon size={15} strokeWidth={2} />
              <span>{actionButton.text}</span>
            </button>
          )}

          {/* Status da VM e Contadores - Apenas para Desenvolvedor */}
          {hasCap('can_use_dev_tools') && (
            <>
              {!hideVmStatus && (
                <div
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
                    vmStatus.online
                      ? 'bg-[#e7f8ef] dark:bg-emerald-900/30 text-[#16855c] dark:text-emerald-400 border-[#c7edd6] dark:border-emerald-900'
                      : 'bg-[#fbe0e0] dark:bg-red-900/30 text-[#c0392b] dark:text-red-400 border-[#f6cfcf] dark:border-red-900'
                  }`}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: vmStatus.online ? '#1eb673' : '#c0392b' }}
                  />
                  <span>VM</span>
                  {vmStatus.online
                    ? <Wifi size={12} strokeWidth={2} />
                    : <WifiOff size={12} strokeWidth={2} />
                  }
                  <span>{vmStatus.online ? 'Online' : 'Offline'}</span>
                </div>
              )}

              {statusCounts !== null && (
                <button
                  onClick={() => navigate('/automacoes/painel')}
                  className="flex items-center gap-2 transition-opacity hover:opacity-80 border-r border-[#e4e9f0] dark:border-border-faint"
                  title="Ir para Execuções das Automações"
                  type="button"
                  style={{ paddingRight: 12, marginRight: 4 }}
                >
                  {statusCounts.running > 0 && (
                    <div
                      className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-[5px] bg-[#eff6ff] dark:bg-blue-900/30 text-[#2563eb] dark:text-blue-400"
                    >
                      <Loader2 size={13} className="animate-spin" />
                      <span>{statusCounts.running}</span>
                    </div>
                  )}
                  {statusCounts.failed > 0 && (
                    <div
                      className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-[5px] animate-pulse bg-[#fbe0e0] dark:bg-red-900/30 text-[#c0392b] dark:text-red-400"
                    >
                      <XCircle size={13} />
                      <span>{statusCounts.failed}</span>
                    </div>
                  )}
                </button>
              )}
            </>
          )}

          {showCompanySelector && companyFilterApplies && (
            <div className="w-[260px]">
              <CompanyMultiSelect
                companies={availableCompanies}
                value={selectedCompanyIds}
                onChange={setSelectedCompanyIds}
              />
            </div>
          )}
          <AlertBell />
          <AccountMenu user={user} onLogout={logout} footerSlot={<ThemeToggle />} />
        </div>
      </div>
      </header>
    </>
  );
};

export default Header;
